import { useLocation } from 'react-router-dom';
import { useSettings } from '@/hooks/useProducts';
import { SITE_NAME, SITE_URL, DEFAULT_LOCALE, SUPPORTED_LOCALES, IS_MULTILINGUAL } from '@/config/site';
import { localizedPath, parseLocalizedPath } from '@/lib/localizedPath';

interface DocumentHeadProps {
  title: string;
  description?: string;
  ogImage?: string;
  ogType?: string;
  canonicalUrl?: string;
  noIndex?: boolean;
  /**
   * Optional per-locale slug map for slug-bearing pages. Keys are locale
   * codes; values are the slug for that locale (without the locale prefix
   * — `localizedPath` adds it). Pages that don't pass this fall back to the
   * current pathname for every alternate, which is correct for static
   * routes (no slugs to translate) but wrong for /product/:slug etc. —
   * those pages should fetch and pass the per-locale slugs explicitly.
   */
  alternateSlugs?: Record<string, string>;
}

export default function DocumentHead({
  title,
  description,
  ogImage,
  ogType = 'website',
  canonicalUrl,
  noIndex,
  alternateSlugs,
}: DocumentHeadProps) {
  const { data: settingsData } = useSettings();
  const { pathname } = useLocation();
  const defaultOgImage = settingsData?.settings?.default_og_image;
  const resolvedImage = ogImage || defaultOgImage;

  const fullTitle = `${title} | ${SITE_NAME}`;
  const fullCanonical = canonicalUrl
    ? canonicalUrl.startsWith('http') ? canonicalUrl : `${SITE_URL}${canonicalUrl}`
    : undefined;

  // Build hreflang alternates. For multilingual tenants we emit one alternate
  // per supported locale plus an `x-default` pointing at the default locale.
  // For slug-bearing routes the caller supplies per-locale slugs; for static
  // routes (Home, Shop, Blog list, etc.) we strip the active locale prefix
  // off the current pathname and re-prefix per locale.
  const alternates = IS_MULTILINGUAL
    ? buildAlternateLinks(pathname, alternateSlugs)
    : null;

  return (
    <>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* OpenGraph */}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:type" content={ogType} />
      {fullCanonical && <meta property="og:url" content={fullCanonical} />}
      {resolvedImage && <meta property="og:image" content={resolvedImage} />}
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter Card */}
      <meta name="twitter:card" content={resolvedImage ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      {resolvedImage && <meta name="twitter:image" content={resolvedImage} />}

      {/* Canonical */}
      {fullCanonical && <link rel="canonical" href={fullCanonical} />}

      {/* hreflang alternates — multilingual tenants only */}
      {alternates}
    </>
  );
}

function buildAlternateLinks(
  pathname: string,
  alternateSlugs: Record<string, string> | undefined,
): React.ReactElement[] {
  const { path: bareWithoutLocale } = parseLocalizedPath(pathname);
  const links: React.ReactElement[] = [];

  for (const locale of SUPPORTED_LOCALES) {
    // Slug-bearing pages: caller passes a per-locale slug map. We append the
    // slug to whatever path comes before it (e.g. /product or /blog).
    let pathForLocale = bareWithoutLocale;
    if (alternateSlugs && alternateSlugs[locale]) {
      const segments = bareWithoutLocale.split('/').filter(Boolean);
      // Replace the last segment (the slug) with the locale-specific one.
      if (segments.length > 0) {
        segments[segments.length - 1] = alternateSlugs[locale];
        pathForLocale = `/${segments.join('/')}`;
      }
    }
    const href = `${SITE_URL}${localizedPath(pathForLocale, locale)}`;
    links.push(<link key={locale} rel="alternate" hrefLang={locale} href={href} />);
  }

  // x-default points search engines at the source-language URL when none of
  // their user's locales match. Per Google guidance, use the default locale.
  const defaultPath = (alternateSlugs && alternateSlugs[DEFAULT_LOCALE]) || bareWithoutLocale;
  links.push(
    <link
      key="x-default"
      rel="alternate"
      hrefLang="x-default"
      href={`${SITE_URL}${localizedPath(defaultPath, DEFAULT_LOCALE)}`}
    />,
  );

  return links;
}
